import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { useThemeClasses } from "../../providers/ThemeProvider";

export interface ShiftModalShellProps {
  title: string;
  /** Rendered under the title — machine, date, shift, headline figures. */
  subtitle?: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  /** Tailwind max-width class; wider for timelines than for charts. */
  widthClass?: string;
}

/**
 * Shared chrome for the shift drill-down popups: portal, backdrop, Escape to
 * close, scroll lock, header with a close button.
 *
 * Sits at z-[10100] — above the sidebar (z-[10001]) and the date-range picker
 * (z-[10000]), which a modal has to cover.
 */
const ShiftModalShell: React.FC<ShiftModalShellProps> = ({
  title,
  subtitle,
  onClose,
  children,
  widthClass = "max-w-5xl",
}) => {
  const themeClasses = useThemeClasses();

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[10100] flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`w-full ${widthClass} rounded-lg border shadow-2xl ${themeClasses.bgSurface} ${themeClasses.borderPrimary}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div
          className={`flex items-start justify-between gap-4 px-5 py-4 border-b ${themeClasses.borderPrimary}`}
        >
          <div>
            <h2 className={`text-lg font-semibold ${themeClasses.textPrimary}`}>
              {title}
            </h2>
            {subtitle && (
              <p className={`text-sm ${themeClasses.textTertiary}`}>
                {subtitle}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            // Static hover class: Tailwind scans source text, so an
            // interpolated `hover:${...}` would never be generated.
            className={`shrink-0 rounded px-2 py-1 text-xl leading-none hover:bg-black/10 dark:hover:bg-white/10 ${themeClasses.textTertiary}`}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
};

export default ShiftModalShell;
